import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useMemo } from 'react';

interface OnboardingProgress {
  cashback_configured: boolean;
  terms_accepted: boolean;
  nuvemshop_integrated: boolean;
  sms_template_configured: boolean;
  completed_at: string | null;
}

export const useOnboardingProgress = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Memoizar user.id para evitar re-renders desnecessários
  const userId = useMemo(() => user?.id, [user?.id]);

  const { data: progress, isLoading } = useQuery({
    queryKey: ['onboarding-progress', userId],
    queryFn: async () => {
      if (!userId) {
        console.warn('❌ No user ID found for onboarding progress');
        return null;
      }
      
      console.log('🔍 Fetching onboarding progress for user:', userId);
      
      const { data, error } = await supabase
        .from('onboarding_progress' as any)
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching onboarding progress:', error);
        return null;
      }

      console.log('✅ Onboarding progress found:', data);
      return (data as unknown as OnboardingProgress) || null;
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes - dados de onboarding não mudam frequentemente
    refetchOnWindowFocus: false, // CRÍTICO: evita re-fetch no Alt+Tab
    refetchOnMount: false, // Evita re-fetch ao montar componente
    retry: 3
  });

  const updateProgress = async (updates: Partial<OnboardingProgress>) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('onboarding_progress' as any)
        .upsert({
          user_id: userId,
          ...updates,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      // Atualizar cache do React Query diretamente
      queryClient.setQueryData(['onboarding-progress', userId], (oldData: OnboardingProgress | null) => ({
        ...oldData,
        ...updates,
      } as OnboardingProgress));
      
    } catch (error: any) {
      console.error('Erro ao atualizar progresso:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o progresso",
        variant: "destructive",
      });
    }
  };

  const completeOnboarding = async () => {
    await updateProgress({
      completed_at: new Date().toISOString(),
    });
  };

  const refetch = () => {
    return queryClient.invalidateQueries({ queryKey: ['onboarding-progress', userId] });
  };

  return {
    progress,
    isLoading,
    updateProgress,
    completeOnboarding,
    refetch,
  };
};